/**
 * "Trazer agrupamentos do cliente" na visão Por Agrupamento.
 *
 * A semeadura automática só acontece no CREATE do orçamento. Este botão é o que
 * alcança os orçamentos que já existiam quando o template do cliente foi
 * cadastrado — ou que foram criados por importação/duplicação, caminhos em que
 * a semeadura não roda de propósito.
 *
 * Só ADICIONA o que falta. Nunca renomeia nem remove: o template é a sugestão
 * do cliente, não a verdade sobre o que já foi montado neste orçamento.
 */
import { useMemo } from "react";
import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import { useQuery } from "react-query";
import Swal from "sweetalert2";

import ClientSpreadsheetService from "../../../../services/clientSpreadsheet.service";
import {
  BudgetGroupTemplateEntry,
  ClientSpreadsheetRecord,
} from "../../../../types/clientSpreadsheet.type";
import { normalizeGroupName } from "../../../../utils/budget-groups";

export interface ExistingGroup {
  name?: string;
  calcOption?: string;
}

export interface MissingGroup {
  name: string;
  calcOption: string;
}

/**
 * O que o template tem e o orçamento não. A identidade é o par (nome, forma de
 * cobrança): o mesmo nome em duas formas de cobrança são dois agrupamentos,
 * porque a tela mostra uma forma por vez.
 */
export function findMissingGroups(
  template: BudgetGroupTemplateEntry[] | undefined,
  existing: ExistingGroup[] | undefined
): MissingGroup[] {
  if (!template?.length) return [];

  const key = (name: string, calcOption: string) =>
    `${normalizeGroupName(name)}::${calcOption}`;

  const known = new Set(
    (existing ?? [])
      .filter((group) => group?.name && group?.calcOption)
      .map((group) => key(String(group.name), String(group.calcOption)))
  );

  const missing: MissingGroup[] = [];
  template.forEach((entry) => {
    const name = entry?.name?.trim();
    if (!name) return;

    (entry.calcOptions ?? []).forEach((calcOption) => {
      const groupKey = key(name, calcOption);
      if (known.has(groupKey)) return;
      known.add(groupKey);
      missing.push({ name, calcOption });
    });
  });

  return missing;
}

/** "5 - LOJAS (A, B)" — uma linha por nome, com as formas de cobrança juntas. */
export function describeMissingGroups(missing: MissingGroup[]): string[] {
  const byName = new Map<string, string[]>();
  missing.forEach(({ name, calcOption }) => {
    byName.set(name, [...(byName.get(name) ?? []), calcOption]);
  });

  const described: string[] = [];
  byName.forEach((calcOptions, name) => {
    described.push(`${name} (${calcOptions.join(", ")})`);
  });
  return described;
}

type Props = {
  clientId?: string;
  existingGroups: ExistingGroup[] | undefined;
  disabled: boolean;
  onImport: (groups: MissingGroup[]) => void;
};

export default function ImportGroupsFromClient({
  clientId,
  existingGroups,
  disabled,
  onImport,
}: Props) {
  const { data: record, isLoading } = useQuery<ClientSpreadsheetRecord | null>({
    queryKey: ["client-spreadsheet", clientId],
    queryFn: async () => {
      try {
        return await ClientSpreadsheetService.getRecord(clientId as string);
      } catch (err: any) {
        // Cliente sem template e o caso comum, nao um erro a reportar.
        if (err?.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: !!clientId && !disabled,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    onError: () => undefined,
  });

  const template = record?.budgetGroupTemplate;

  const missing = useMemo(
    () => findMissingGroups(template, existingGroups),
    [template, existingGroups]
  );

  // Cliente sem template ou sem agrupamentos padrao: o botao nem aparece, para
  // nao prometer uma acao que nao tem o que fazer.
  if (disabled || isLoading || !template?.length) return null;

  const nothingToImport = missing.length === 0;

  const confirm = () => {
    // `text`, não `html`: é o que os outros diálogos do orçamento usam, e o
    // nome do agrupamento vem de quem cadastrou o template — interpolar isso
    // em innerHTML seria colocar conteúdo de usuário na marcação sem motivo.
    Swal.fire({
      title: `Trazer ${missing.length} agrupamento(s) do cliente?`,
      text: `${describeMissingGroups(missing).join(
        ", "
      )}. Nenhum agrupamento existente é removido ou renomeado.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, trazer",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      onImport(missing);
    });
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Tooltip
        title={
          nothingToImport
            ? "O orçamento já tem todos os agrupamentos do template do cliente"
            : "Cria os agrupamentos da planilha padrão do cliente que ainda faltam"
        }
      >
        <span>
          <Button
            fullWidth
            variant="outlined"
            size="small"
            disabled={nothingToImport}
            startIcon={
              isLoading ? (
                <CircularProgress size={14} />
              ) : (
                <LibraryAddIcon fontSize="small" />
              )
            }
            onClick={confirm}
            sx={{
              fontSize: "14px",
              textTransform: "none",
              color: "#1361A4",
              borderColor: "#1361A4",
              "& .MuiButton-startIcon": { margin: 0, mr: 0.5 },
            }}
          >
            {nothingToImport
              ? "Agrupamentos do cliente já trazidos"
              : `Trazer agrupamentos do cliente (${missing.length})`}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}
